import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Divider, Row, Select, Spin } from 'antd';
import type { SelectProps } from 'antd';
import { debounce, get } from 'lodash';
import { i18n as i } from '../../i18n';

export interface InfiniteScrollSelectLoadParams {
  page: number;
  pageSize: number;
  searchValue: string;
}

interface InfiniteScrollSelectProps
  extends Omit<SelectProps, 'options' | 'onSearch' | 'dropdownRender'> {
  loadMore: (params: InfiniteScrollSelectLoadParams, isSearch: boolean) => Promise<void> | void;
  messageKey?: string;
  optionKey?: string;
  options: unknown[];
  pageSize?: number;
  total: number;
  valueKey?: string;
}

const InfiniteScrollSelect: React.FC<InfiniteScrollSelectProps> = ({
  loadMore,
  messageKey,
  optionKey = 'id',
  options = [],
  pageSize = 10,
  total,
  valueKey = 'displayName',
  ...restProps
}) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await loadMore({ page, pageSize, searchValue }, false);
      setPage(page + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSearch = async (value: string) => {
    setLoading(true);
    setSearchValue(value);
    try {
      await loadMore({ page: 1, pageSize, searchValue: value }, true);
      setPage(2);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((value: string) => handleSelectSearch(value), 500),
    [],
  );

  useEffect(() => {
    handleLoadMore();
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const renderDropdown = (menu: React.ReactNode) => (
    <Spin spinning={loading}>
      <div>
        {menu}
        {options.length > 0 && (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <div
              style={{ padding: '4px 8px', textAlign: 'center' }}
              onMouseDown={(event) => event.preventDefault()}
            >
              {options.length < total ? (
                <Button type="link" onClick={handleLoadMore}>
                  {i('加载更多')}
                </Button>
              ) : (
                <span style={{ color: 'rgba(0,0,0,.45)' }}>{i('已加载全部')}</span>
              )}
            </div>
          </>
        )}
      </div>
    </Spin>
  );

  const handleChange: SelectProps['onChange'] = (value, option) => {
    if (!value) {
      handleSelectSearch('');
    }
    restProps.onChange?.(value, option);
  };

  return (
    <Select
      {...restProps}
      filterOption={false}
      loading={loading}
      onChange={handleChange}
      onSearch={debouncedSearch}
      dropdownRender={renderDropdown}
      optionLabelProp="label"
    >
      {options.map((option) => {
        const value = get(option, optionKey, '');
        const label = get(option, valueKey, '');
        return (
          <Select.Option value={value} key={value} label={label}>
            <Row gutter={5} align="middle">
              <Col
                span={messageKey ? 16 : 24}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={label}
              >
                {label}
              </Col>
              {messageKey && (
                <Col
                  span={8}
                  style={{
                    overflow: 'hidden',
                    textAlign: 'right',
                    color: 'rgba(0,0,0,.45)',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={get(option, messageKey, '')}
                >
                  {get(option, messageKey, '')}
                </Col>
              )}
            </Row>
          </Select.Option>
        );
      })}
    </Select>
  );
};

export default InfiniteScrollSelect;
